import { StorageConstants } from '@linkie/shared';
import { create, type StateCreator } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import localforage from 'localforage';
import { debounce } from 'lodash';
import { createPlaygroundStoreInitializer } from '@linkie/shared';
import type { PlaygroundStore, PlaygroundState, PlaygroundAction } from '@linkie/shared';

import { isLoggedIn } from '@/core/utils/is-logged-in';
import { isPublicPageRoute } from '@/core/utils/is-public-page-route';

export type { PlaygroundStore } from '@linkie/shared';

export const playgroundStoreName = StorageConstants.PLAYGROUND_STORE;

const indexedDBStorage = createJSONStorage(() => ({
  getItem: async (name): Promise<string | null> => {
    const value = await localforage.getItem(name);
    return value ? JSON.stringify(value) : null;
  },
  setItem: async (name, value: string) => {
    if (isLoggedIn()) return;

    try {
      const parsed = JSON.parse(value);
      await localforage.setItem(name, parsed);
    } catch (error) {
      console.error('[PlaygroundStore] Failed to parse value for IndexedDB', error);
    }
  },
  removeItem: async (name) => {
    await localforage.removeItem(name);
  },
}));

const createPlaygroundStore = (): StateCreator<PlaygroundStore> => {
  const baseStore = createPlaygroundStoreInitializer();

  if (isLoggedIn() || isPublicPageRoute()) {
    return baseStore;
  }

  return persist(baseStore, {
    name: playgroundStoreName,
    version: 0,
    storage: indexedDBStorage,
    onRehydrateStorage: () => (state) => {
      console.log('[PlaygroundStore] Hydrated', state);
    },
  }) as unknown as StateCreator<PlaygroundStore>;
};

export const usePlaygroundStore = create<PlaygroundStore>()(devtools(createPlaygroundStore()));

export const usePlaygroundState = <T>(selector: (state: PlaygroundState) => T): T =>
  usePlaygroundStore((store) => selector(store.state));

export const usePlaygroundDispatch = () => usePlaygroundStore((store) => store.dispatch);

export const dispatchWithDebounce = debounce((action: PlaygroundAction) => {
  usePlaygroundStore.getState().dispatch(action);
}, 300);
